import {
  Avatar,
  Box,
  Button,
  Grid,
  LinearProgress,
  Link,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitle,
  Slider,
  Snackbar,
  Typography,
} from '@cambridgeassessment/cambridge-ui';
// eslint-disable-next-line prettier/prettier
import AddIcon from '@material-ui/icons/Add';
// eslint-disable-next-line prettier/prettier
import PublishIcon from '@material-ui/icons/Publish';
import RemoveIcon from '@material-ui/icons/Remove';
import React, { memo, useCallback, useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { FileRejection, useDropzone } from 'react-dropzone';
import { MAX_FILE_UPLOAD_SIZE } from 'src/constants';
import { getAccountInfo, publishPhoto } from 'src/services/gigya/gigya-service';
import { useAppContext } from '../AppContext';
import { useStyles } from './styles';

export type PhotoUploadModalProps = {
  open: boolean;
  onClose: () => void;
  updatePhoto: (accountInfo: any) => void;
};

const PhotoUploadModal: React.FC<PhotoUploadModalProps> = ({
  open,
  onClose,
  updatePhoto,
}) => {
  const { setAppState } = useAppContext();
  const classes = useStyles();
  const editor = useRef(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [image, setImage] = useState(null);
  const [imageRejected, setImageRejected] = useState<boolean>(false);
  const [scale, setScale] = useState<number>(1);

  const closeModal = (): void => {
    onClose();
    setTimeout(() => resetValues(), 100);
  };

  const resetValues = (): void => {
    setImage(null);
    setImageRejected(false);
    setScale(1);
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        setImage(acceptedFiles[0]);
        setImageRejected(false);
      }

      if (rejectedFiles.length > 0) {
        setImageRejected(true);
      }
    },
    []
  );

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    maxSize: MAX_FILE_UPLOAD_SIZE,
    maxFiles: 1,
    accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpg', '.jpeg'],
      'image/gif': ['.gif'],
    },
    noDragEventsBubbling: true,
    noKeyboard: true,
  });

  const fileRejectionMessage = useCallback(() => {
    let errorMessage = 'SORRY! There was a problem uploading file';
    fileRejections.forEach(({ file, errors }) => {
      errorMessage = '';
      errors.forEach((error) => {
        if (error.code === 'file-invalid-type') {
          errorMessage = `${file.name} has invalid file type`;
          return;
        }

        if (error.code === 'file-too-large') {
          errorMessage = `File size of  ${file.name} exceeds beyond the limit`;
        }
      });
    });

    return errorMessage;
  }, [fileRejections]);

  const handleScaleChange = (value) => {
    setScale(value);
  };

  const handleSaveChanges = (): void => {
    const croppedImage = editor.current
      ?.getImageScaledToCanvas()
      ?.toDataURL()
      ?.split(',')[1];

    setLoading(true);
    publishPhoto(croppedImage).then((response) => {
      setLoading(false);
      if (response.status === 'OK' && response.errorCode === 0) {
        getAccountInfo(
          (_response) => {
            resetValues();
            closeModal();
            const updateData = {
              ..._response,
              profile: {
                ..._response?.profile,
                photoURL: `${
                  _response?.profile?.photoURL
                }?ts=${new Date().toISOString()}`,
              },
            };
            updatePhoto(updateData);
            setAppState((prevState) => ({
              ...prevState,
              profilePhotoUrl: `${
                _response?.profile?.photoURL
              }?ts=${new Date().toISOString()}`,
            }));
          },
          ['profile']
        );
      } else {
        setImageRejected(true);
      }
    });
  };

  const renderAvatarEditor = (): JSX.Element => {
    return (
      <Grid container>
        <Grid item lg={12} md={12} sm={12} xs={12} className={classes.flexGrid}>
          <AvatarEditor
            ref={editor}
            width={150}
            height={150}
            borderRadius={75}
            color={[10, 10, 10, 0.5]}
            scale={scale}
            image={image}
            backgroundColor="#ff0000"
            data-testid="avatarEditor"
          />
        </Grid>
        <Grid item lg={12} md={12} sm={12} xs={12} className={classes.flexGrid}>
          <Box mt={1}>
            <RemoveIcon
              data-testid="zoomIn"
              onClick={() =>
                scale > 1
                  ? setScale((previousValue) => previousValue - 1)
                  : null
              }
            />
          </Box>
          <Box pl={1} pr={1}>
            <Slider
              value={typeof scale === 'number' ? scale : 0}
              onChange={handleScaleChange}
              aria-labelledby="input-slider"
              data-testid="zoomSlider"
              min={1}
              max={5}
              step={0.25}
            />
          </Box>
          <Box mt={1}>
            <AddIcon
              data-testid="zoomOut"
              onClick={() => setScale((previousValue) => previousValue + 1)}
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderDropzoneEditor = (): JSX.Element => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        p={3}
        className={classes.zoneContent}
        {...getRootProps()}
      >
        <Avatar>
          <PublishIcon />
        </Avatar>

        <Box mt={5} display="flex" flexDirection="column" alignItems="center">
          <Typography variant="button">
            Drag and drop photo here or{' '}
            <Link>
              select from your device
              <input
                data-testid="photoUpload-modal-link"
                {...getInputProps()}
              />
            </Link>
          </Typography>
          <Box mt={3} display="flex" flexDirection="column" alignItems="center">
            <Typography>
              Supported file types: JPG(.jpg), PNG(.png), GIF(.gif)
            </Typography>
            <Typography>The maximum file size is 6MB</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Modal
      open={open}
      onClose={(): void => closeModal()}
      size="large"
      disableEscapeKeyDown
      disableBackdropClick
      closeAfterTransition
      data-testid="photoUpload-modal"
      className={classes.modalWindow}
    >
      <ModalTitle
        onClose={(): void => closeModal()}
        data-testid="photoUpload-modalTitle"
      >
        Upload profile photo
      </ModalTitle>
      <ModalContent className={classes.modalContent}>
        {loading && (
          <Grid container>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <LinearProgress
                color="primary"
                className={classes.linearProgress}
                data-dataTestId="photo-upload-linear-progress"
                data-testid="photoUpload-linear-progress"
              />
            </Grid>
          </Grid>
        )}
        {!loading && (
          <Box data-testid="modal-content-photo-upload">
            {image ? renderAvatarEditor() : renderDropzoneEditor()}
            {imageRejected && (
              <Snackbar
                open={imageRejected}
                variant="error"
                message={fileRejectionMessage()}
                data-testid="photoUpload-errorSnackbar"
                autoHideDuration={3000}
                onClick={() => {
                  setImageRejected(false);
                }}
              />
            )}
          </Box>
        )}
      </ModalContent>
      <ModalActions>
        {!loading && (
          <>
            {image ? (
              <Button
                color="primary"
                variant="contained"
                onClick={(): void => setImage(null)}
                data-testid="photoUpload-clearButton"
              >
                Clear
              </Button>
            ) : (
              <Button
                color="primary"
                variant="contained"
                onClick={(): void => closeModal()}
                data-testid="photoUpload-cancelButton"
              >
                Cancel
              </Button>
            )}
            <Button
              color="primary"
              onClick={() => handleSaveChanges()}
              data-testid="photoUpload-saveButton"
              disabled={!image}
            >
              Save Changes
            </Button>
          </>
        )}
      </ModalActions>
    </Modal>
  );
};

export default memo(PhotoUploadModal);
